import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactJson from 'react-json-view';
import SplitterLayout from 'react-splitter-layout';
import 'react-splitter-layout/lib/index.css';
import { NetworkManager } from './NetworkManager';
import { CommonUtil } from '../utils/CommonUtil';

interface JSONCompareViewerProps {
    data: any;
    compareData?: any[]; // Optional array of data to compare against
    onDataChange?: (newData: any) => void;
    title?: string;
    editable?: boolean;
    documentId?: string | number;
    documentType?: 'invoice' | 'statement' | 'payment';
    allowFullscreen?: boolean;
}

const EMPTY_COMPARE_DATA: any[] = [];

const JSONCompareViewer: React.FC<JSONCompareViewerProps> = ({
    data,
    compareData = EMPTY_COMPARE_DATA,
    onDataChange,
    title = "JSON Viewer",
    editable = true,
    documentId,
    documentType,
    allowFullscreen = true
}) => {
    const [currentData, setCurrentData] = useState(data);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        setCurrentData(data);
    }, [data]);

    const downloadPDF = useCallback(async () => {
        if (!documentId) return;

        const orgId = CommonUtil.getSelectedOrgId();
        if (!orgId) return;

        const headers = new Headers({ "Content-Type": "application/json" });
        const token = localStorage.getItem("token");
        if (token) headers.append("Authorization", `Bearer ${token}`);

        const url = documentType === "statement"
            ? `api/download/vHubStatement?statementId=${documentId}`
            : `api/download/vHubInvoice?invoiceId=${documentId}`;

        try {
            const response = await fetch(`${NetworkManager.serverPath}${url}`, {
                method: "GET",
                headers,
            });
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${title || 'document'}.pdf`;
            link.click();
        } catch (error) {
            console.error("PDF download failed:", error);
        }
    }, [documentId, documentType, title]);

    const findMismatches = (obj1: any, obj2: any, path: string = ''): string[] => {
        let diffs: string[] = [];
        const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

        keys.forEach(key => {
            const currentPath = path ? `${path}.${key}` : key;
            const val1 = obj1 ? obj1[key] : undefined;
            const val2 = obj2 ? obj2[key] : undefined;

            if (typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null) {
                diffs = [...diffs, ...findMismatches(val1, val2, currentPath)];
            } else if (val1 !== val2) {
                diffs.push(currentPath);
            }
        });

        return diffs;
    };

    const mismatches = useMemo(() => {
        if (compareData && compareData.length > 0) {
            return findMismatches(currentData, compareData[0]);
        }
        return [];
    }, [currentData, compareData]);

    const handleEdit = (edit: any) => {
        setCurrentData(edit.updated_src);
        if (onDataChange) {
            onDataChange(edit.updated_src);
        }
    };

    const handleAdd = (add: any) => {
        setCurrentData(add.updated_src);
        if (onDataChange) {
            onDataChange(add.updated_src);
        }
    };

    const handleDelete = (del: any) => {
        setCurrentData(del.updated_src);
        if (onDataChange) {
            onDataChange(del.updated_src);
        }
    };

    const renderViewer = (src: any, name: string, isEditable: boolean) => (
        <div className="json-viewer-pane p-2" style={{ height: '100%', overflow: 'auto' }}>
            <ReactJson
                src={src}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={true}
                enableClipboard={true}
                onEdit={isEditable ? handleEdit : false}
                onAdd={false}
                onDelete={false}
                // collapsed={2}
                name={false}
                style={{ borderRadius: '4px', padding: '10px' }}
            />
        </div>
    );

    return (
        <div className={`json-compare-viewer ${isFullscreen ? 'fullscreen-viewer' : ''}`} 
            style={isFullscreen ? {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999,
                backgroundColor: 'white',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column'
            } : { 
                height: '600px', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            }}>
            <style>
                {`
                    .json-compare-viewer .splitter-layout {
                        position: relative;
                        flex: 1;
                    }
                    .json-compare-viewer .splitter-layout .layout-pane {
                        overflow: auto;
                    }
                `}
            </style>
            
            <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
                <div className="d-flex align-items-center gap-2">
                    
                    <span className="fw-bold">{title} {isFullscreen ? '(Fullscreen)' : ''}</span>
                    {documentId && (
                        <i 
                            className="fa-solid fa-download text-primary cursor-pointer ms-1" 
                            style={{ cursor: 'pointer' }}
                            onClick={downloadPDF}
                            title="Download Invoice PDF"
                        ></i>
                    )}
                </div>
                {allowFullscreen && (
                    <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        title={isFullscreen ? "Exit Fullscreen" : "Expand Outside"}
                    >
                        <i className={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
                    </button>
                )}
            </div>

            {mismatches.length > 0 && (
                <div className="alert alert-warning p-2 mb-0" style={{ fontSize: '12px' }}>
                    <strong>Mismatches found:</strong>
                    <ul className="mb-0 mt-1" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {mismatches.slice(0, 10).map((m, i) => <li key={i}>{m}</li>)}
                        {mismatches.length > 10 && <li>... and {mismatches.length - 10} more</li>}
                    </ul>
                </div>
            )}
            
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                {compareData.length > 0 ? (
                    <SplitterLayout primaryIndex={0} percentage={true} secondaryInitialSize={50}>
                        {renderViewer(currentData, "Original / Edited", editable)}
                        {renderViewer(compareData[0], "Comparison Target", false)}
                    </SplitterLayout>
                ) : (
                    renderViewer(currentData, title, editable)
                )}
            </div>
        </div>
    );
};

export default JSONCompareViewer;
